<template>
  <section class="overview-submenu">
    <div class="overview-submenu__left">
      <div class="overview-submenu__title">Standard Event Stats</div>
      <!-- PDF and other reports feature coming soon but not ready yet -->
      <!-- <Button outlined :disabled="true">
        <IconReport />
        Reports
      </Button>
      <ComingSoon /> -->
    </div>
    <div class="overview-submenu__right" v-if="!editMode">
      <div class="benchmark-controls">
        <FeatureFlag name="basic_benchmarking_widgets">
          <InputToggle v-model="analyticsStore.isBenchmarking" name="benchmarking">
            <template #label>
              <div class="benchmark-label">
                <span>Activate Benchmarking</span>
                <Tooltip>
                  <template v-slot:text>
                    <IconInfo />
                  </template>
                  <template v-slot:tooltip>
                    Activate Benchmarking to see the benchmarking data for your event.
                  </template>
                </Tooltip>
              </div>
            </template>
          </InputToggle>
        </FeatureFlag>
      </div>

      <div class="copy-edit-buttons">
        <Button
          v-if="analyticsStore.chosenEvent && userStore.isStaff"
          severity="secondary"
          aria-label="Copy UUID"
          @click="copyToClipboard(toast, analyticsStore.chosenEvent.uuid)"
        >
          <template #iconLeft><IconCopy /></template>
          Copy UUID
        </Button>
        <Tooltip v-if="!editMode" position="left">
          <template v-slot:text>
            <Button
              severity="secondary"
              @click="startEditMode"
              :disabled="analyticsStore.isDemoEventSelected && userStore.isNonStaff"
            >
              <template #iconLeft><IconEdit /></template>
            </Button>
          </template>
          <template v-slot:tooltip>
            <template v-if="analyticsStore.isDemoEventSelected && userStore.isNonStaff">
              You do not have permissions to edit the layout for this event
            </template>
            <template v-else> Edit metrics </template>
          </template>
        </Tooltip>
      </div>
    </div>
    <div class="overview-submenu__right" v-else>
      <Button v-if="editMode" severity="secondary" @click="cancelChanges">Cancel Changes</Button>
      <Button v-if="editMode" severity="primary" @click="saveTemplate">
        <template #iconLeft><IconSave /></template>
        Save Changes
      </Button>
      <MetricAdd
        v-if="editMode"
        :disabled="false"
        v-model:widgets="analyticsStore.chosenWidgets"
        @onAddedWidget="onAddedWidget"
        @onRemovedWidget="onRemovedWidget"
        @onDone="onDone"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import Button from '@/components/global/Button.vue';
import FeatureFlag from '@/components/global/FeatureFlag.vue';
import InputToggle from '@/components/inputs/InputToggle.vue';
import Tooltip from '@/components/global/Tooltip.vue';
import MetricAdd from '@/components/dialogs/MetricAdd.vue';

import IconCopy from '@/icons/events/IconCopy.svg?component';
import IconEdit from '@/icons/IconEdit.svg?component';
import IconInfo from '@/icons/IconInfo.svg?component';
import IconSave from '@/icons/analytics/IconSave.svg?component';

import { useToastStore } from '@/stores/toast';
const toast = useToastStore();

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

import { useAnalyticsStore } from '@/stores/analytics';
const analyticsStore = useAnalyticsStore();

defineProps({
  editMode: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits([
  'onEditMode',
  'onCancelChanges',
  'onSaveTemplate',
  'onRemovedWidget',
  'onAddedWidget',
  'onDone',
]);

const copyToClipboard = (toast: any, uuid: string) => {
  navigator.clipboard.writeText(uuid);
  toast.manual('success', 'UUID copied to clipboard');
};

const startEditMode = () => {
  emits('onEditMode');
};

const cancelChanges = () => {
  emits('onCancelChanges');
};

const saveTemplate = () => {
  emits('onSaveTemplate');
};

const onAddedWidget = (widget: any) => {
  emits('onAddedWidget', widget);
};

const onRemovedWidget = (widget: any) => {
  emits('onRemovedWidget', widget);
};

const onDone = () => {
  emits('onDone');
};
</script>

<style lang="scss" scoped>
.overview-submenu {
  background-color: #ffffff;
  border: 1px solid #e4e6eb;
  border-radius: 8px;
  padding: 24px 24px 24px 24px;

  @media screen and (min-width: 768px) {
    @include mixins.flex-center();
    margin-bottom: variables.$sizing-lg;
  }

  .overview-submenu__left {
    margin-bottom: variables.$sizing-xs;

    @media screen and (min-width: 768px) {
      @include mixins.flex-center($gap: variables.$sizing-xxxs);
      margin-bottom: 0;
      margin-right: auto;
    }
  }

  .overview-submenu__right {
    display: grid;
    gap: variables.$sizing-xs;
    margin-left: auto;

    @media screen and (min-width: 768px) {
      @include mixins.flex-center($gap: variables.$sizing-xxxs);
    }

    .copy-edit-buttons {
      display: flex;
      gap: variables.$sizing-xxs;
      margin: 0.4rem 0;

      @media screen and (min-width: 768px) {
        gap: variables.$sizing-xxxs;
        margin: 0;
      }
    }
  }

  .overview-submenu__title {
    @include typography.font-display($size: xs, $weight: medium);
    color: variables.$primitives-color-tertiary-500;
    margin-right: variables.$sizing-md;
  }
}

.benchmark-controls {
  align-items: center;
  display: flex;
  gap: variables.$sizing-xs;
  margin-right: variables.$sizing-xs;
}

.copy-edit-buttons {
  display: flex;
  gap: variables.$sizing-xxxs;
  margin: 0.4rem 0;

  @media screen and (min-width: 768px) {
    gap: variables.$sizing-xxxs;
    margin: 0;
  }
}

.benchmark-label {
  align-items: center;
  display: flex;
  gap: variables.$sizing-xxs;

  .premium-icon {
    fill: variables.$primitives-color-secondary-500;
    height: variables.$sizing-sm;
    width: variables.$sizing-sm;
  }
}
</style>
